import { useEffect, useState } from "react";
import { X, History, Plus, Trash2, Send, MessageSquare, Paperclip, CheckSquare, Repeat } from "lucide-react";
import {
  api, formatApiError, CLASSIFICATION_LABELS, PRIORITY_LABELS, VISIBILITY_LABELS,
  TASK_STATUS_LABELS, CLASS_STYLE, PRIO_STYLE, STATUS_STYLE, BOARD_STATUS_LABELS,
} from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Download, Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = {
  title: "", description: "", classification: "O", priority: "medium", visibility: "project",
  assignee_id: "", sprint_id: "", available_to_claim: false, due_date: "", story_points: "", estimated_hours: "",
};

// Single reusable component for create / view / edit of a task, used by
// Backlog, Kanban and Sprint screens. taskId=null => create mode.
export default function TaskModal({ projectId, project, members, sprints, taskId, defaultSprintId, defaultColumnId, onClose, onSaved }) {
  const { user } = useAuth();
  const [currentId, setCurrentId] = useState(taskId || null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ ...emptyForm, sprint_id: defaultSprintId || "" });
  const [comment, setComment] = useState("");
  const [newCheck, setNewCheck] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const isCreate = !currentId;
  const isAdminMgr = !!project?.can_admin;
  const activeSprints = (sprints || []).filter((s) => !["archived", "completed", "cancelled"].includes(s.status));

  const loadDetail = async (id) => {
    try { const { data } = await api.get(`/tasks/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); onClose?.(); }
  };

  useEffect(() => { if (currentId) loadDetail(currentId); /* eslint-disable-next-line */ }, [currentId]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const patchTask = async (partial) => {
    try {
      const { data } = await api.patch(`/tasks/${currentId}`, partial);
      setDetail((prev) => ({ ...prev, ...data })); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); loadDetail(currentId); }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { title: form.title, description: form.description, classification: form.classification, priority: form.priority };
      if (isAdminMgr) { payload.visibility = form.visibility; if (form.assignee_id) payload.assignee_id = form.assignee_id; }
      if (form.sprint_id) payload.sprint_id = form.sprint_id;
      if (form.due_date) payload.due_date = form.due_date;
      if (form.story_points !== "") payload.story_points = Number(form.story_points);
      if (form.estimated_hours !== "") payload.estimated_hours = Number(form.estimated_hours);
      if (form.available_to_claim) payload.available_to_claim = true;
      const { data } = await api.post(`/projects/${projectId}/tasks`, payload);
      toast.success("Tarefa criada.");
      onSaved?.();
      setCurrentId(data.id); // switch to view/edit; reveals comments, attachments, history
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const claim = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/tasks/${currentId}/claim`); toast.success("Tarefa assumida."); setDetail(data); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); loadDetail(currentId); }
    finally { setBusy(false); }
  };

  const sendToKanban = async () => {
    try { await api.post(`/tasks/${currentId}/send-to-kanban`, defaultColumnId ? { column_id: defaultColumnId } : {}); toast.success("Enviada ao Kanban."); loadDetail(currentId); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const returnToBacklog = async () => {
    try { const { data } = await api.post(`/tasks/${currentId}/return-to-backlog`); setDetail(data); toast.success("Devolvida ao Backlog."); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreArchived = async () => {
    try { const { data } = await api.post(`/tasks/${currentId}/restore-archived`); setDetail(data); toast.success("Restaurada ao Backlog."); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addChecklist = () => {
    if (!newCheck.trim()) return;
    const list = [...(detail.checklist || []), { text: newCheck.trim(), done: false }];
    setNewCheck(""); patchTask({ checklist: list });
  };
  const toggleCheck = (i) => {
    const list = (detail.checklist || []).map((c, idx) => idx === i ? { ...c, done: !c.done } : c);
    patchTask({ checklist: list });
  };
  const removeCheck = (i) => patchTask({ checklist: (detail.checklist || []).filter((_, idx) => idx !== i) });

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Arquivo excede o limite de 20 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tasks/${currentId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Anexo enviado.");
      await loadDetail(currentId);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const downloadAttachment = async (a) => {
    try {
      const resp = await api.get(`/tasks/${currentId}/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; document.body.appendChild(link); link.click();
      link.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeAttachment = async (a) => {
    try { await api.delete(`/tasks/${currentId}/attachments/${a.id}`); toast.success("Anexo removido."); await loadDetail(currentId); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/tasks/${currentId}/comments`, { text: comment.trim() }); setComment(""); loadDetail(currentId); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canEdit = detail?.can_edit;
  const progress = detail?.checklist_progress ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="task-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-xl font-semibold tracking-tight">{isCreate ? "Nova tarefa" : (detail?.title || "Tarefa")}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="task-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {isCreate ? (
          <form onSubmit={submitCreate} className="grid md:grid-cols-3 gap-6 p-6" data-testid="task-create-form">
            <div className="md:col-span-2 space-y-4">
              <input required placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title"
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <textarea rows={6} placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <p className="text-xs text-slate-400">Checklist, comentários, anexos e histórico ficam disponíveis após criar a tarefa.</p>
            </div>
            <div className="space-y-3">
              <Prop label="Classificação (E/D/O/Q)">
                <select value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} data-testid="task-classification" className="prop-select">
                  {Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                </select>
              </Prop>
              <Prop label="Prioridade">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority" className="prop-select">
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Prop>
              <Prop label="Sprint">
                <select value={form.sprint_id} onChange={(e) => setForm({ ...form, sprint_id: e.target.value })} data-testid="task-sprint" className="prop-select">
                  <option value="">Sem sprint</option>
                  {activeSprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Prop>
              {isAdminMgr && (
                <>
                  <Prop label="Visibilidade">
                    <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="task-visibility" className="prop-select">
                      {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Prop>
                  <Prop label="Responsável">
                    <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee" className="prop-select">
                      <option value="">Sem responsável</option>
                      {(project?.participants || []).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                    </select>
                  </Prop>
                </>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Prop label="Story points"><input type="number" min="0" value={form.story_points} onChange={(e) => setForm({ ...form, story_points: e.target.value })} data-testid="task-points" className="prop-select" /></Prop>
                <Prop label="Horas est."><input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} data-testid="task-hours" className="prop-select" /></Prop>
              </div>
              <Prop label="Prazo"><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" className="prop-select" /></Prop>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.available_to_claim} onChange={(e) => setForm({ ...form, available_to_claim: e.target.checked })} data-testid="task-available" />
                Disponível para assumir
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
                <button type="submit" disabled={busy} data-testid="task-submit" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">Criar tarefa</button>
              </div>
            </div>
          </form>
        ) : !detail ? (
          <div className="p-10 text-center text-slate-500">Carregando...</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 p-6">
            {/* Main area */}
            <div className="md:col-span-2 space-y-5">
              <input disabled={!canEdit} value={detail.title} onChange={(e) => setDetail({ ...detail, title: e.target.value })} onBlur={(e) => canEdit && e.target.value !== detail.title && patchTask({ title: e.target.value })} data-testid="detail-title"
                className="w-full text-lg font-semibold rounded-lg border border-transparent hover:border-slate-200 focus:border-slate-300 px-2 py-1 focus:outline-none disabled:bg-transparent" />
              <textarea disabled={!canEdit} rows={4} placeholder="Sem descrição." defaultValue={detail.description} onBlur={(e) => canEdit && e.target.value !== detail.description && patchTask({ description: e.target.value })} data-testid="detail-description"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50" />

              {/* Checklist */}
              <div data-testid="detail-checklist">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><CheckSquare className="w-4 h-4" /> Checklist</div>
                  <span className="text-xs text-slate-500">{progress}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} /></div>
                <ul className="space-y-1">
                  {(detail.checklist || []).map((c, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm group">
                      <input type="checkbox" checked={!!c.done} disabled={!canEdit} onChange={() => toggleCheck(i)} data-testid={`check-${i}`} />
                      <span className={c.done ? "line-through text-slate-400" : "text-slate-700"}>{c.text}</span>
                      {canEdit && <button onClick={() => removeCheck(i)} className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </li>
                  ))}
                </ul>
                {canEdit && (
                  <div className="flex gap-2 mt-2">
                    <input value={newCheck} onChange={(e) => setNewCheck(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addChecklist()} placeholder="Novo item" data-testid="check-input" className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-sm" />
                    <button onClick={addChecklist} data-testid="check-add" className="h-8 px-2 rounded-md border border-slate-200 text-sm hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
                  </div>
                )}
                {/* Mandatory-for-completion toggle (admin/manager only) */}
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <label className={`inline-flex items-start gap-2 text-sm ${detail.can_admin ? "text-slate-700 cursor-pointer" : "text-slate-400 cursor-not-allowed"}`} title={detail.can_admin ? "" : "Apenas administrador ou gerente pode alterar"}>
                    <input type="checkbox" className="mt-0.5" checked={!!detail.checklist_required} disabled={!detail.can_admin}
                      onChange={(e) => patchTask({ checklist_required: e.target.checked })} data-testid="checklist-required" />
                    <span>Obrigatório para conclusão da tarefa</span>
                  </label>
                  {detail.checklist_required && (detail.checklist_pending || 0) > 0 && (
                    <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1" data-testid="checklist-required-pending">
                      <AlertTriangle className="w-3 h-3" /> {detail.checklist_pending} item(ns) pendente(s) impedem a conclusão.
                    </p>
                  )}
                </div>
              </div>

              {/* Attachments */}
              <div data-testid="detail-attachments">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2"><Paperclip className="w-4 h-4" /> Anexos</div>
                <ul className="space-y-1">
                  {(detail.attachments || []).map((a) => {
                    const canDel = canEdit || a.uploaded_by === String(user?.id);
                    return (
                      <li key={a.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded-md px-2 py-1.5" data-testid={`attachment-${a.id}`}>
                        <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-slate-800">{a.filename}</div>
                          <div className="text-[10px] text-slate-400">{Math.max(1, Math.round((a.size || 0) / 1024))} KB · {a.uploaded_by_name}</div>
                        </div>
                        <button onClick={() => downloadAttachment(a)} data-testid={`att-download-${a.id}`} className="text-slate-400 hover:text-slate-800" title="Baixar"><Download className="w-4 h-4" /></button>
                        {canDel && <button onClick={() => removeAttachment(a)} data-testid={`att-delete-${a.id}`} className="text-slate-400 hover:text-red-600" title="Remover"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </li>
                    );
                  })}
                  {(detail.attachments || []).length === 0 && <li className="text-xs text-slate-400">Nenhum anexo.</li>}
                </ul>
                {canEdit && (
                  <label className={`mt-2 inline-flex items-center gap-2 h-8 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50 ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="att-upload-label">
                    <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Anexar arquivo"}
                    <input type="file" className="hidden" onChange={uploadAttachment} data-testid="att-file-input" />
                  </label>
                )}
                {canEdit && <p className="text-[10px] text-slate-400 mt-1">Máx. 20 MB. Tipos executáveis bloqueados.</p>}
              </div>

              {/* Comments */}
              <div data-testid="detail-comments">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2"><MessageSquare className="w-4 h-4" /> Comentários</div>
                <ul className="space-y-2 mb-2">
                  {(detail.comments || []).map((c) => (
                    <li key={c.id} className="text-sm bg-slate-50 rounded-lg px-3 py-2">
                      <div className="text-xs text-slate-500"><span className="font-medium text-slate-700">{c.by_name}</span> · {new Date(c.at).toLocaleString("pt-BR")}</div>
                      <div className="text-slate-700">{c.text}</div>
                    </li>
                  ))}
                  {(detail.comments || []).length === 0 && <li className="text-xs text-slate-400">Sem comentários.</li>}
                </ul>
                <div className="flex gap-2">
                  <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Escreva um comentário" data-testid="comment-input" className="flex-1 h-9 rounded-lg border border-slate-200 px-3 text-sm" />
                  <button onClick={postComment} data-testid="comment-submit" className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm">Enviar</button>
                </div>
              </div>

              {/* History */}
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2"><History className="w-4 h-4" /> Histórico</div>
                <ul className="space-y-1 max-h-40 overflow-auto" data-testid="detail-history">
                  {(detail.history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="text-xs text-slate-600"><span className="font-medium">{h.by_name || "—"}</span> · {h.action}{h.detail ? ` (${h.detail})` : ""} · {new Date(h.at).toLocaleString("pt-BR")}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Properties */}
            <div className="space-y-3" data-testid="detail-properties">
              <div className="flex flex-wrap gap-1.5">
                <span className={`text-xs px-2 py-0.5 rounded ${CLASS_STYLE[detail.classification]}`}>Classe {detail.classification}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${PRIO_STYLE[detail.priority]}`}>{PRIORITY_LABELS[detail.priority]}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLE[detail.status]}`}>{TASK_STATUS_LABELS[detail.status]}</span>
                {detail.sprint_name && <span className="text-xs px-2 py-0.5 rounded text-white" style={{ backgroundColor: detail.sprint_color || "#64748b" }}>{detail.sprint_name}</span>}
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{detail.in_kanban ? `Kanban — ${detail.board_column_name}` : "No Backlog"}</span>
                {detail.impediment_active && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3" /> Impedida</span>}
              </div>
              {detail.recurrence_id && (
                <div className="text-xs bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 text-violet-800" data-testid="detail-recurrence">
                  <div className="font-semibold inline-flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> Tarefa recorrente</div>
                  <div className="mt-0.5">{detail.recurrence_name || "Modelo"} · ocorrência #{detail.recurrence_seq} · programada para {detail.recurrence_scheduled_date}</div>
                  {detail.assignment_needed && <div className="mt-1 inline-flex items-center gap-1 text-rose-700"><AlertTriangle className="w-3 h-3" /> Atribuição necessária</div>}
                </div>
              )}
              {detail.impediment_active && detail.impediment_reason && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800" data-testid="detail-impediment">
                  <span className="font-semibold">Impedimento:</span> {detail.impediment_reason}
                </div>
              )}
              <Prop label="Classificação"><select disabled={!canEdit} value={detail.classification} onChange={(e) => patchTask({ classification: e.target.value })} data-testid="detail-classification" className="prop-select">{Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}</select></Prop>
              <Prop label="Prioridade"><select disabled={!canEdit} value={detail.priority} onChange={(e) => patchTask({ priority: e.target.value })} data-testid="detail-priority" className="prop-select">{Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Prop>
              <Prop label="Status">
                {detail.in_kanban ? (
                  <div data-testid="detail-status-readonly" className="prop-select bg-slate-50 flex items-center text-slate-600 cursor-not-allowed">
                    {BOARD_STATUS_LABELS[detail.board_status] || detail.board_column_name || TASK_STATUS_LABELS[detail.status]}
                    <span className="ml-auto text-[10px] text-slate-400">via Kanban</span>
                  </div>
                ) : (
                  <select disabled={!canEdit} value={detail.status} onChange={(e) => patchTask({ status: e.target.value })} data-testid="detail-status" className="prop-select">{Object.entries(TASK_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                )}
              </Prop>
              <Prop label="Responsável">
                <select disabled={!isAdminMgr} value={detail.assignee_id || ""} onChange={(e) => patchTask(e.target.value ? { assignee_id: e.target.value } : { clear_assignee: true })} data-testid="detail-assignee" className="prop-select">
                  <option value="">Sem responsável</option>
                  {(project?.participants || []).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                </select>
              </Prop>
              <Prop label="Sprint">
                <select disabled={!isAdminMgr} value={detail.sprint_id || ""} onChange={(e) => patchTask(e.target.value ? { sprint_id: e.target.value } : { clear_sprint: true })} data-testid="detail-sprint" className="prop-select">
                  <option value="">Sem sprint</option>
                  {activeSprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  {detail.sprint_id && !activeSprints.find((s) => s.id === detail.sprint_id) && <option value={detail.sprint_id}>{detail.sprint_name}</option>}
                </select>
              </Prop>
              <Prop label="Prazo"><input disabled={!canEdit} type="date" value={detail.due_date ? detail.due_date.slice(0, 10) : ""} onChange={(e) => patchTask({ due_date: e.target.value })} data-testid="detail-due" className="prop-select" /></Prop>
              <div className="grid grid-cols-2 gap-2">
                <Prop label="Story points"><input disabled={!canEdit} type="number" min="0" defaultValue={detail.story_points ?? ""} onBlur={(e) => canEdit && patchTask({ story_points: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid="detail-points" className="prop-select" /></Prop>
                <Prop label="Horas est."><input disabled={!canEdit} type="number" min="0" step="0.5" defaultValue={detail.estimated_hours ?? ""} onBlur={(e) => canEdit && patchTask({ estimated_hours: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid="detail-hours" className="prop-select" /></Prop>
              </div>
              {isAdminMgr && (
                <Prop label="Visibilidade"><select value={detail.visibility} onChange={(e) => patchTask({ visibility: e.target.value })} data-testid="detail-visibility" className="prop-select">{Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Prop>
              )}
              {(detail.can_edit || isAdminMgr) && detail.status === "open" && !detail.assignee_id && (
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!detail.available_to_claim} onChange={(e) => patchTask({ available_to_claim: e.target.checked })} data-testid="detail-available" />
                  Disponível para assumir
                </label>
              )}
              <div className="flex flex-col gap-2 pt-2">
                {detail.can_claim && <button onClick={claim} disabled={busy} data-testid="detail-claim" className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">Assumir tarefa</button>}
                {canEdit && !detail.in_kanban && !detail.archived && <button onClick={sendToKanban} data-testid="detail-send-kanban" className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><Send className="w-4 h-4" /> Enviar ao Kanban</button>}
                {canEdit && detail.in_kanban && !detail.archived && <button onClick={returnToBacklog} data-testid="detail-return-backlog" className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">Devolver ao Backlog</button>}
                {detail.archived && isAdminMgr && <button onClick={restoreArchived} data-testid="detail-restore" className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">Restaurar ao Backlog</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Prop = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
    {children}
  </label>
);
